import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { JwtPayload } from '../../models/jwt-payload.interface';

/**
 * Serviço para decodificar e extrair informações do JWT (Access Token)
 */
@Injectable({
  providedIn: 'root',
})
export class JwtTokenService {
  private readonly TOKEN_KEY = 'accessToken';

  constructor() {}

  // DECODIFICAÇÃO

  decodeAccessToken(): JwtPayload | null {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return null;
      }

      return jwtDecode<JwtPayload>(token);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  getTokenPayload(): JwtPayload | null {
    return this.decodeAccessToken();
  }

  // VALIDAÇÃO & EXPIRAÇÃO

  isTokenValid(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    return !this.isTokenExpired();
  }

  isTokenExpired(): boolean {
    const payload = this.decodeAccessToken();
    if (!payload?.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  getTokenExpirationDate(): Date | null {
    const payload = this.decodeAccessToken();
    if (!payload?.exp) {
      return null;
    }

    return new Date(payload.exp * 1000);
  }

  getTokenIssuedDate(): Date | null {
    const payload = this.decodeAccessToken();
    if (!payload?.iat) {
      return null;
    }

    return new Date(payload.iat * 1000);
  }

  /** Retorna segundos restantes até expiração */
  getTimeUntilExpiration(): number {
    const payload = this.decodeAccessToken();
    if (!payload?.exp) {
      return 0;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeRemaining = payload.exp - currentTime;

    return Math.max(0, timeRemaining);
  }

  // DADOS DO USUÁRIO

  getUserCode(): string | null {
    return this.decodeAccessToken()?.code || null;
  }

  getUserName(): string | null {
    return this.decodeAccessToken()?.name || null;
  }

  /** Nome completo com unidade/departamento */
  getDisplayName(): string | null {
    return this.decodeAccessToken()?.displayName || null;
  }

  getUserEmail(): string | null {
    return this.decodeAccessToken()?.email || null;
  }

  getUsername(): string | null {
    return this.decodeAccessToken()?.username || null;
  }

  getUserSubject(): string | null {
    return this.decodeAccessToken()?.sub || null;
  }

  getUserTitle(): string | null {
    return this.decodeAccessToken()?.title || null;
  }

  getUserCompany(): string | null {
    return this.decodeAccessToken()?.company || null;
  }

  getUserDepartment(): string | null {
    return this.decodeAccessToken()?.department || null;
  }

  // AUTORIZAÇÃO - ROLES

  getUserRoles(): string[] {
    return this.decodeAccessToken()?.roles || [];
  }

  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some((role) => userRoles.includes(role));
  }

  hasAllRoles(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.every((role) => userRoles.includes(role));
  }

  /** Retorna o primeiro role formatado (remove prefixo XCXP_SISMET_ e capitaliza apenas primeira letra) */
  getFormattedRole(): string | null {
    const roles = this.getUserRoles();
    if (roles.length === 0) {
      return null;
    }

    const firstRole = roles[0];
    const roleWithoutPrefix = firstRole.replace('XCXP_SISMET_', '');

    // Converte para minúscula e capitaliza apenas a primeira letra
    return (
      roleWithoutPrefix.charAt(0).toUpperCase() +
      roleWithoutPrefix.slice(1).toLowerCase()
    );
  }

  // AUTORIZAÇÃO - GROUPS

  getUserGroups(): string[] {
    return this.decodeAccessToken()?.groups || [];
  }

  hasGroup(group: string): boolean {
    const groups = this.getUserGroups();
    return groups.includes(group);
  }

  isInDevGroup(): boolean {
    return this.hasGroup('CN=SISMET-DEV-ADM');
  }

  // INFORMAÇÕES DA APLICAÇÃO

  getAppName(): string | null {
    return this.decodeAccessToken()?.app || null;
  }

  getProvider(): string | null {
    return this.decodeAccessToken()?.provider || null;
  }

  getTokenType(): string | null {
    return this.decodeAccessToken()?.type || null;
  }

  // PRIVADO

  private getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }
}
